// What a promo code preview (POST /Payment/promo-codes/preview) is worth saying.
//
// Two things about it are easy to get wrong:
//
//   - Its amounts are in minor units: 9900 is €99.00, and a currency without
//     cents (JPY) has no hundredths to divide by. The currency says which.
//   - A code is not applied now. A coupon's duration starts when it is applied,
//     and a free trial would use most of it up, so the server holds the code and
//     applies it when the trial converts (`appliedAt: "trial_end"`). The wording
//     says "your first paid month", never "applied now".
//
// Its amounts are before tax: Stripe adds tax to the invoice itself.

const DEFAULT_CURRENCY = "usd";

const currencyFormatter = (currency) => {
   const code = String(currency || DEFAULT_CURRENCY).toUpperCase();

   try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: code });
   } catch (invalidCurrency) {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
   }
};

/** An amount in minor units as money, or null when there is no amount. */
export const formatMinorUnits = (amount, currency) => {
   if (typeof amount !== "number" || Number.isNaN(amount)) return null;

   const formatter = currencyFormatter(currency);
   const fractionDigits = formatter.resolvedOptions().maximumFractionDigits;

   return formatter.format(amount / 10 ** fractionDigits);
};

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

// How long the discount lasts. Every invoice is a month's, the annual plan's
// too (billed monthly on a 12-month commitment), so a code used once comes off
// the first paid month whichever plan is picked.
const describeDuration = (preview) => {
   switch (preview.duration) {
      case "once":
         return "your first paid month";
      case "repeating":
         return typeof preview.durationInMonths === "number"
            ? `your first ${plural(preview.durationInMonths, "paid month")}`
            : "your first paid months";
      case "forever":
         return "every month";
      default:
         return "your subscription";
   }
};

// "25% off", "€10.00 off", or null when the preview names neither.
const describeDiscount = (preview) => {
   if (preview.discountType === "percent" && typeof preview.percentOff === "number") {
      return `${preview.percentOff}% off`;
   }

   const amount = formatMinorUnits(preview.amountOff, preview.currency);
   return amount ? `${amount} off` : null;
};

/**
 * A valid preview as the lines it is shown in.
 *
 * `trialing` is whether the subscription this code is for starts on (or is
 * still in) a free trial. A code held for the trial's end is described that
 * way only when there is a trial for it to wait for.
 *
 * @returns {{headline: string, timing: string, amounts: string|null}}
 */
export const describePromoPreview = (preview, { trialing = true } = {}) => {
   const discount = describeDiscount(preview);
   const scope = describeDuration(preview);
   const headline = discount ? `${discount} ${scope}` : `A discount on ${scope}`;

   const timing =
      preview.appliedAt === "trial_end" && trialing
         ? "Applied when your free trial ends, not before."
         : "Applied to your first paid invoice.";

   const total = formatMinorUnits(preview.firstInvoiceTotal, preview.currency);
   const subtotal = formatMinorUnits(preview.firstInvoiceSubtotal, preview.currency);
   const hasDiscount = typeof preview.firstInvoiceDiscount === "number" && preview.firstInvoiceDiscount > 0;

   let amounts = null;
   if (total) {
      amounts =
         hasDiscount && subtotal
            ? `Your first paid invoice: ${total} instead of ${subtotal}, plus any applicable taxes.`
            : `Your first paid invoice: ${total}, plus any applicable taxes.`;
   }

   return { headline, timing, amounts };
};
